import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { ArrowLeft, Send, Phone, Video, MoreVertical, Clock, X, AlertTriangle, Paperclip, CornerUpLeft, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import SessionExtension from '../../components/SessionExtension';
import SessionFeedback from '../../components/SessionFeedback';
import SessionResources from '../../components/SessionResources';

export default function MobileChatInterface() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showExtension, setShowExtension] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [otherTyping, setOtherTyping] = useState(false);
  const [showJump, setShowJump] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const typingHeartbeatRef = useRef(0);
  const lastMsgKeyRef = useRef(null);
  const pollInterval = useRef(null);

  const myId = user?.id || user?.masked_id;

  useEffect(() => {
    fetchSession();
    fetchMessages();

    // Poll messages + typing every 2s (REST fallback since WS isn't available externally)
    pollInterval.current = setInterval(async () => {
      await fetchMessages();
      try {
        const token = localStorage.getItem('token');
        const tp = await axios.get(`${API}/sessions/${sessionId}/typing`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOtherTyping(!!tp.data?.typing);
      } catch (_) { /* non-blocking */ }
    }, 2000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    isAtBottomRef.current = true;
    setShowJump(false);
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    isAtBottomRef.current = atBottom;
    if (atBottom) setShowJump(false);
  }, []);

  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    const key = `${messages.length}:${last.id}`;
    if (key === lastMsgKeyRef.current) return;
    const isFirstRender = lastMsgKeyRef.current === null;
    lastMsgKeyRef.current = key;
    if (isFirstRender) { scrollToBottom(false); return; }
    const mine = last && (last.sender_id === myId);
    if (isAtBottomRef.current || mine) scrollToBottom(true);
    else setShowJump(true);
  }, [messages, myId, scrollToBottom]);

  const pingTyping = useCallback((typing) => {
    const now = Date.now();
    if (typing && now - typingHeartbeatRef.current < 1500) return;
    typingHeartbeatRef.current = now;
    const token = localStorage.getItem('token');
    axios.post(`${API}/sessions/${sessionId}/typing?is_typing=${typing}`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(response.data);
      
      // Check if session is completed
      if (response.data.status === 'completed') {
        setSessionEnded(true);
        clearInterval(pollInterval.current);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    const replyId = replyTo?.id || null;
    setNewMessage('');
    setReplyTo(null);
    pingTyping(false);
    isAtBottomRef.current = true;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/messages`,
        {
          session_id: sessionId,
          content: messageContent,
          reply_to: replyId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Immediately fetch to show the new message
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(messageContent); // Restore message on error
    }
  };

  const startVoiceCall = () => {
    if (session?.webrtc_enabled) {
      navigate(`/mobile-voice-call/${sessionId}`);
    } else {
      toast.error('Voice calls not available for this session');
    }
  };

  const startVideoCall = () => {
    if (session?.webrtc_enabled && session?.webrtc_mode === 'video') {
      navigate(`/mobile-video-call/${sessionId}`);
    } else {
      toast.error('Video calls not available for this session');
    }
  };

  const endSession = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/sessions/${sessionId}/status?status=completed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionEnded(true);
      clearInterval(pollInterval.current);
      toast.success('Session ended');
      
      // Show feedback modal
      setShowFeedback(true);
    } catch (error) {
      toast.error('Failed to end session');
    }
  };

  const handleFeedbackClose = () => {
    setShowFeedback(false);
    navigate('/mobile-chats');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E5DDD5]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#E5DDD5] flex flex-col">
      {/* Header */}
      <div className="bg-primary text-white p-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate('/mobile-chats')} className="p-1" data-testid="back-button">
          <ArrowLeft size={24} />
        </button>
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <span className="text-sm font-semibold">{session?.grade_code || 'CS'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-medium truncate" data-testid="session-title">
            {session?.grade_code ? `${session.grade_code} Session` : 'Chat Session'}
          </h2>
          <p className="text-xs opacity-80">
            {sessionEnded ? 'Session ended' : session?.status === 'active' ? 'Connected' : 'Waiting for counselor...'}
          </p>
        </div>
        <div className="flex gap-1">
          {session?.webrtc_enabled && session?.channel === 'voice' && (
            <button onClick={startVoiceCall} className="p-2 rounded-full hover:bg-white/10" data-testid="voice-call-btn">
              <Phone size={20} />
            </button>
          )}
          {session?.webrtc_enabled && session?.channel === 'video' && (
            <button onClick={startVideoCall} className="p-2 rounded-full hover:bg-white/10" data-testid="video-call-btn">
              <Video size={20} />
            </button>
          )}
          <button 
            onClick={() => setShowMenu(!showMenu)} 
            className="p-2 rounded-full hover:bg-white/10"
            data-testid="menu-btn"
          >
            <MoreVertical size={20} />
          </button>
        </div>
      </div>

      {/* Dropdown Menu */}
      {showMenu && (
        <div className="absolute right-4 top-16 bg-white rounded-xl shadow-lg border border-gray-100 z-20 overflow-hidden">
          <button
            onClick={() => { setShowExtension(true); setShowMenu(false); }}
            className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            data-testid="extend-session-menu"
          >
            <Clock size={16} className="text-primary" />
            Extend Session
          </button>
          <button
            onClick={() => { setShowResources(true); setShowMenu(false); }}
            className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            data-testid="resources-menu"
          >
            <Paperclip size={16} className="text-primary" />
            Shared resources
          </button>
          <button
            onClick={() => { navigate('/tele-manas'); setShowMenu(false); }}
            className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
          >
            <AlertTriangle size={16} className="text-red-500" />
            Crisis Support
          </button>
          <button
            onClick={() => { endSession(); setShowMenu(false); }}
            className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
            data-testid="end-session-menu"
          >
            End Session
          </button>
        </div>
      )}

      {/* Session Extension Panel */}
      {showExtension && !sessionEnded && (
        <div className="px-4 py-2 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#2D3748]">Extend Session</span>
            <button onClick={() => setShowExtension(false)} className="p-1">
              <X size={18} className="text-[#718096]" />
            </button>
          </div>
          <SessionExtension 
            sessionId={sessionId} 
            currentGrade={session?.grade_code}
            onExtensionRequested={() => setShowExtension(false)}
          />
        </div>
      )}

      {/* Mood Tags Display */}
      {session?.mood_tags && session.mood_tags.length > 0 && (
        <div className="px-4 py-2 bg-white/80 border-b border-gray-100">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[#718096]">Concerns:</span>
            {session.mood_tags.map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full capitalize">
                {tag.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 relative" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
        {/* System message for waiting */}
        {session?.status === 'queued' && messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Clock size={32} className="text-primary" />
            </div>
            <h3 className="font-medium text-[#2D3748] mb-2">Waiting for a counselor</h3>
            <p className="text-sm text-[#718096]">
              You'll be connected shortly. Feel free to share what's on your mind.
            </p>
          </div>
        )}

        {messages.length === 0 && session?.status !== 'queued' && (
          <div className="text-center text-[#718096] py-12">
            <p>Start the conversation by typing a message</p>
          </div>
        )}

        {messages.map((message) => {
          if (message.sender_role === 'system') {
            return (
              <div key={message.id} data-testid={`message-${message.id}`} className="flex justify-center">
                <div className="max-w-[85%] text-center text-xs text-[#7A625C] bg-[#FDF2F0] border border-[#FAC7C0] rounded-2xl px-3 py-2 whitespace-pre-line" data-testid="system-message">
                  {message.content}
                </div>
              </div>
            );
          }
          const isCurrentUser = message.sender_id === myId;
          const quoted = message.reply_to ? messages.find((m) => m.id === message.reply_to) : null;
          return (
            <div
              key={message.id}
              data-testid={`message-${message.id}`}
              className={`flex items-end gap-1 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
            >
              {isCurrentUser && !sessionEnded && (
                <button type="button" aria-label="Reply" data-testid={`reply-btn-${message.id}`} onClick={() => setReplyTo(message)} className="text-[#A0AEC0] active:text-primary p-1 self-center">
                  <CornerUpLeft size={16} />
                </button>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  isCurrentUser
                    ? 'bg-primary text-white rounded-br-md'
                    : 'bg-white text-[#2D3748] rounded-bl-md shadow-sm'
                }`}
              >
                {!isCurrentUser && (
                  <p className="text-xs font-medium text-primary mb-1">Counselor</p>
                )}
                {quoted && (
                  <div className="mb-1 border-l-2 border-current/40 pl-2 text-xs opacity-80 line-clamp-2" data-testid={`quoted-${message.id}`}>
                    {quoted.sender_role === 'system' ? 'Automated message' : (quoted.content || '')}
                  </div>
                )}
                <p className="text-sm leading-relaxed break-words">{message.content}</p>
                <p className={`text-[10px] text-right mt-1 ${isCurrentUser ? 'text-white/70' : 'text-[#A0AEC0]'}`}>
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              {!isCurrentUser && !sessionEnded && (
                <button type="button" aria-label="Reply" data-testid={`reply-btn-${message.id}`} onClick={() => setReplyTo(message)} className="text-[#A0AEC0] active:text-primary p-1 self-center">
                  <CornerUpLeft size={16} />
                </button>
              )}
            </div>
          );
        })}

        {otherTyping && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="bg-white rounded-2xl rounded-bl-md shadow-sm px-4 py-3 flex items-center gap-1">
              <span className="w-2 h-2 bg-[#7A625C] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-[#7A625C] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-[#7A625C] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />

        {showJump && (
          <button
            type="button"
            data-testid="jump-to-latest-btn"
            onClick={() => scrollToBottom(true)}
            className="sticky bottom-2 float-right mr-1 z-30 flex items-center gap-1 px-3 py-2 rounded-full bg-[#7D202B] text-white text-xs shadow-lg active:bg-[#963A42]"
          >
            <ArrowDown size={14} /> New messages
          </button>
        )}
      </div>

      {/* Session Ended Banner */}
      {sessionEnded && (
        <div className="bg-amber-50 border-t border-amber-200 p-4 text-center">
          <p className="text-sm text-amber-800 font-medium">This session has ended</p>
          <button
            onClick={() => setShowFeedback(true)}
            className="text-sm text-primary underline mt-1"
            data-testid="give-feedback-btn"
          >
            Give feedback
          </button>
        </div>
      )}

      {/* Input */}
      {!sessionEnded && (
        <div className="bg-[#F0F0F0] p-3">
          {replyTo && (
            <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-white border-l-4 border-[#7D202B]" data-testid="reply-preview">
              <CornerUpLeft size={16} className="text-[#7D202B] shrink-0" />
              <div className="min-w-0 flex-1 text-xs text-[#7A625C]">
                <div className="font-medium text-[#7D202B]">Replying to</div>
                <div className="truncate">{replyTo.sender_role === 'system' ? 'Automated message' : replyTo.content}</div>
              </div>
              <button type="button" onClick={() => setReplyTo(null)} data-testid="reply-cancel" aria-label="Cancel reply" className="p-1 text-[#A0AEC0]">
                <X size={16} />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => setShowResources(true)}
              data-testid="open-resources-btn"
              aria-label="Shared resources"
              className="w-11 h-11 rounded-full bg-white text-[#7D202B] flex items-center justify-center flex-shrink-0"
            >
              <Paperclip size={20} />
            </button>
            <form onSubmit={sendMessage} className="flex-1 flex gap-2">
              <textarea
                data-testid="message-input"
                value={newMessage}
                onChange={(e) => { setNewMessage(e.target.value); pingTyping(e.target.value.length > 0); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
                placeholder="Type a message..."
                className="flex-1 bg-white rounded-2xl px-4 py-3 resize-none max-h-24 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                rows="1"
                style={{ minHeight: '44px' }}
              />
              <button
                type="submit"
                data-testid="send-message-btn"
                disabled={!newMessage.trim()}
                className="w-11 h-11 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 active:scale-95 transition-transform"
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Shared PDF resources */}
      <SessionResources
        sessionId={sessionId}
        user={user}
        isOpen={showResources}
        onClose={() => setShowResources(false)}
      />

      {/* Feedback Modal */}
      {showFeedback && (
        <SessionFeedback
          sessionId={sessionId}
          onClose={handleFeedbackClose}
          onSubmit={handleFeedbackClose}
        />
      )}

      {/* Click outside to close menu */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
}
